import { useCallback, useEffect, useRef, useState } from 'react';
import { APP_BUILD_ID, APP_VERSION } from '../version';

// version.json se regenera en cada build y vive junto a index.html.
const VERSION_URL = `${process.env.PUBLIC_URL || ''}/version.json`;

const DEFAULT_INTERVAL = 2 * 60 * 1000;

/**
 * Compara el buildId compilado en este bundle contra el publicado en el
 * servidor. Si difieren, hay un deploy más nuevo que la pestaña abierta.
 *
 * Revisa cada `interval`, al volver a la pestaña y al recuperar la conexión.
 * `check()` fuerza una revisión y resuelve en true si ya hay versión nueva.
 */
export default function useAppVersion({ interval = DEFAULT_INTERVAL } = {}) {
  const [latest, setLatest] = useState(null);
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    if (inFlight.current) return false;
    inFlight.current = true;
    try {
      // El query param y el no-store evitan que el navegador o el CDN
      // devuelvan la copia vieja del propio version.json.
      const res = await fetch(`${VERSION_URL}?_=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return false;
      const data = await res.json();
      if (!data?.buildId) return false;
      setLatest(data);
      return data.buildId !== APP_BUILD_ID;
    } catch {
      return false; // sin red: se reintenta en el siguiente ciclo
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (APP_BUILD_ID === 'development') return; // no molestar mientras se programa

    check();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, interval);

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', check);
    };
  }, [check, interval]);

  return {
    version: APP_VERSION,
    buildId: APP_BUILD_ID,
    latestVersion: latest?.version ?? null,
    updateAvailable: !!latest?.buildId && latest.buildId !== APP_BUILD_ID,
    check,
  };
}